import Controller from '@ember/controller';
import { action } from '@ember/object';
import { trackedArray } from '@ember/reactive/collections';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { TOAST_TIMEOUT_LONG } from '@mylighthouse/prism-foundation';
import type { Dayjs, ManipulateType, OpUnitType } from 'dayjs';
import dayjs from 'dayjs';
import { task } from 'ember-concurrency';
import { PARITY_STRATEGY_DATA_START } from 'frontend-utils/utils/constants/constant-values';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import type {
  ParityStrategyBreakdown,
  ParityStrategyEvolution,
  ParityStrategyIssue,
} from 'lumen/models';
import type MarketParityStrategyRoute from 'lumen/routes/market/parity/strategy';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithMarket } from 'lumen/services/hotel-and-market';
import { xValue } from 'lumen/utils/charts/chart-utils';
import { DEFAULT_QP_VALUES } from 'lumen/utils/constants/default-qp-values';
import { FILTER_DELIMITER } from 'lumen/utils/constants/general-constants';
import {
  DATE_AGGREGATION_WEEK,
  HISTORICAL_POINTS_AMOUNT,
} from 'lumen/utils/constants/pi-constant-values';
import {
  BENCHMARK,
  DIMENSION_TRANSLATIONS,
  DIMENSIONS,
  type DimensionValue,
  LATEST,
  type ParityFilterOrBenchmark,
} from 'lumen/utils/constants/pi-constant-values';
import getAPIErrorMessage from 'lumen/utils/get-api-error-message';
import { logReportInput } from 'lumen/utils/log-report-input';
import {
  getFormattedLastPossibleExtractDate,
  isExtractDateOutsideHistory,
} from 'lumen/utils/parity-analytics/date-utils';
import { formatUrl } from 'lumen/utils/rates-request';
import type { ModelFrom } from 'lumen/utils/routes';

type ParityStrategyQueryParams = {
  extractDate?: string;
  historicalExtractDate?: string;
};

type BreakdownBlock = {
  data: ParityStrategyBreakdown[];
  formattedDimension: string;
};

const BREAKDOWN_BLOCK_ORDER: Partial<DimensionValue>[] = [
  DIMENSIONS.MARKET,
  DIMENSIONS.BRAND,
  DIMENSIONS.CHANNEL,
  DIMENSIONS.DISCOUNT,
];

// These breakdown blocks should not be shown when not present in the API response
const NO_DATA_BREAKDOWN_BLOCKS: Partial<DimensionValue>[] = [
  DIMENSIONS.MARKET, // "Leaf" markets don't have the market breakdown because they don't have any submarkets
  DIMENSIONS.BRAND, // SH-33677
];

const PROMOTION_BLOCK_ORDER: Partial<DimensionValue>[] = [
  DIMENSIONS.MOBILE,
  DIMENSIONS.LOS,
  DIMENSIONS.MEMBERSHIP,
];

export default class MarketParityStrategyController extends Controller {
  declare model: ModelFrom<MarketParityStrategyRoute>;
  queryParams = ['extractDate', 'historicalExtractDate', 'activeFilters'];

  @service declare access: Services['access'];
  @service declare ajax: AjaxService;
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithMarket;

  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked activeFilters = trackedArray<string>();
  @tracked dateAggregation: ManipulateType = DATE_AGGREGATION_WEEK;
  @tracked extractDate: string =
    DEFAULT_QP_VALUES.market.parity.strategy.extractDate;
  @tracked historicalExtractDate: string =
    DEFAULT_QP_VALUES.market.parity.strategy.historicalExtractDate;
  @tracked isBenchmarkSelected = true;
  @tracked isFilterBarHovered = false;
  @tracked isoDateAggregation: OpUnitType | 'isoWeek' = 'isoWeek';

  BREAKDOWN_BLOCK_ORDER = BREAKDOWN_BLOCK_ORDER;
  breakdownsWithSearchEnabled = [DIMENSIONS.BRAND, DIMENSIONS.MARKET];
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  isSelected = (breakdown: ParityStrategyBreakdown) =>
    this.activeFilters.includes(
      `${breakdown.dimension}${FILTER_DELIMITER}${breakdown.mappedValueId}`,
    );
  PROMOTION_BLOCK_ORDER = PROMOTION_BLOCK_ORDER;
  updateStatusTask = task(
    { drop: true },
    async (issue, newStatus): Promise<void> => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (issue.status !== newStatus) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        issue.status = newStatus;
        try {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          await issue.save();

          this.toast.success('', {
            title: this.intl.t('general.toast.successfully_saved'),
            timeout: TOAST_TIMEOUT_LONG,
            showIcon: true,
          });
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          issue.rollbackAttributes();

          this.toast.alert('', {
            title: this.intl.t('general.error_title'),
            timeout: TOAST_TIMEOUT_LONG,
            showIcon: true,
          });

          this.errorTracking.captureException(error);
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          this.alerting.showErrorAlert({
            text: getAPIErrorMessage(error, this.intl),
          });
        }
      }
    },
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get breakdownBlocks() {
    if (!this.hasBreakdownData) {
      return {} as Record<DimensionValue, BreakdownBlock>;
    }

    const breakdowns = {} as Record<DimensionValue, BreakdownBlock>;
    this.model.breakdowns.value?.breakdowns.forEach((breakdown) => {
      if (breakdowns[breakdown.dimension]) {
        breakdowns[breakdown.dimension].data.push(breakdown);
      } else {
        breakdowns[breakdown.dimension] = {
          formattedDimension: breakdown.formattedDimension,
          data: [breakdown],
        };
      }
    });

    Object.keys(breakdowns).forEach((dimension) => {
      if (dimension !== DIMENSIONS.DISCOUNT) {
        breakdowns[dimension as DimensionValue].data.sort((a, b) => {
          // Sort the data array by lossScoreAsInteger in descending order
          return (b.lossScoreAsInteger ?? 0) - (a.lossScoreAsInteger ?? 0);
        });
      }
    });

    // Sort the breakdowns according to the sortOrder
    const sortedBreakdowns = {} as Record<DimensionValue, BreakdownBlock>;
    BREAKDOWN_BLOCK_ORDER.forEach((dimension) => {
      if (breakdowns[dimension]) {
        sortedBreakdowns[dimension] = breakdowns[dimension];
      } else if (!NO_DATA_BREAKDOWN_BLOCKS.includes(dimension)) {
        sortedBreakdowns[dimension] = {
          formattedDimension: this.intl.t(DIMENSION_TRANSLATIONS[dimension], {
            count: 1,
          }),
          data: [],
        };
      }
    });

    return sortedBreakdowns;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get breakdownData() {
    if (this.isBreakdownRequestRunning) {
      return [];
    }

    return [
      // @ts-expect-error: Incorrect type
      ...(this.model.breakdowns.value?.promotions as ParityStrategyBreakdown[]),
      ...(this.model.breakdowns.value?.breakdowns as ParityStrategyBreakdown[]),
      // @ts-expect-error: Incorrect type
      ...(this.model.breakdowns.value?.hotels as ParityStrategyBreakdown[]),
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currentExtractDate() {
    return this.isLatestSelected ? LATEST : this.extractDate;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dateRange() {
    return {
      start: this.extractDateMoment
        .startOf(this.isoDateAggregation)
        .startOf('day'),
      end: this.extractDateMoment.endOf(this.isoDateAggregation).startOf('day'),
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get extractDateMoment() {
    return this.hotelAndMarketService.toLocalTime(this.extractDate);
  }

  // Only contains the evolutions that should be shown in the evolution chart
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filteredEvolutionsForChart() {
    if (!this.hasEvolutionData) {
      return [];
    }

    if (this.isHistoricalLatestSelected) {
      const numberOfEvolutions = HISTORICAL_POINTS_AMOUNT + 1; // +1 for the latest
      return this.sortedEvolutions.slice(-numberOfEvolutions);
    }

    const lastPeriodIndex = this.sortedEvolutions.findIndex(
      (evolution) => evolution.extractDate === this.historicalExtractDate,
    );

    return this.sortedEvolutions.slice(
      lastPeriodIndex - HISTORICAL_POINTS_AMOUNT + 1,
      lastPeriodIndex + 1,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedExtractRange() {
    if (this.isLatestSelected) {
      return this.intl.t('parity_analytics.headers.shop_results.weekly', {
        formattedExtractDate: this.intl
          .t('benchmark.current_score')
          .toLowerCase(),
      });
    }

    return this.intl.t('parity_analytics.headers.range', {
      formattedExtractDate: dayjs(this.extractDate).format('LL'),
      formattedExtractDateEndOfWeek: dayjs(this.extractDate)
        .add(6, 'day')
        .format('LL'),
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedPeriodRange() {
    if (!this.hasEvolutionData) {
      return null;
    }

    const firstPeriodRange = this
      .filteredEvolutionsForChart[0] as ParityStrategyEvolution;
    const formattedFirstPeriodOfRange =
      firstPeriodRange.extractDateMoment.format('ll');
    const lastPeriodOfRange = this.filteredEvolutionsForChart[
      this.filteredEvolutionsForChart.length - 1
    ] as ParityStrategyEvolution;
    const formattedLastPeriodOfRange = lastPeriodOfRange.isLatest
      ? this.intl.t('general.today')
      : lastPeriodOfRange.extractDateMoment.add(6, 'day').format('ll');

    return `${formattedFirstPeriodOfRange} - ${formattedLastPeriodOfRange}`;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasActiveFilters() {
    return this.activeFilters.length > 0;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasBreakdownData() {
    return !this.isBreakdownRequestRunning && !this.hasBreakdownRequestError;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasBreakdownRequestError() {
    return this.model.breakdowns.isError;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasEvolutionData() {
    return !this.isEvolutionRequestRunning && !this.hasEvolutionRequestError;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasEvolutionRequestError() {
    return this.model.evolutions.isError;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasIssueData() {
    return !this.isIssueRequestRunning && !this.hasIssueRequestError;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasIssueRequestError() {
    return this.model.issues?.isError;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasNoData() {
    return !this.selectedPeriodData || this.selectedPeriodData.isMissing;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get historicalExtractDateMoment() {
    return this.hotelAndMarketService.toLocalTime(this.historicalExtractDate);
  }

  // Used in the PickerPeriod component since for LATEST we also want to shift 8 periods from the latest extract date
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get historicalExtractDateMomentWithFallback() {
    return this.isHistoricalLatestSelected
      ? this.hotelAndMarketService.toLocalTime(
          getFormattedLastPossibleExtractDate(),
        )
      : this.historicalExtractDateMoment;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotelData() {
    if (!this.hasBreakdownData) {
      return [];
    }

    // @ts-expect-error: Incorrect type
    return this.model.breakdowns.value?.hotels.sort((a, b) => {
      if (a.lossScoreAsInteger !== b.lossScoreAsInteger) {
        // Sort the data array by lossScoreAsInteger in descending order by default
        return (b.lossScoreAsInteger ?? 0) - (a.lossScoreAsInteger ?? 0);
      }

      if (a.referenceLossScoreAsInteger !== b.referenceLossScoreAsInteger) {
        // Subsort by referenceLossScoreAsInteger in case the loss scores are identical
        return b.referenceLossScoreAsInteger - a.referenceLossScoreAsInteger;
      }

      // Subsort alphabetically in case the loss and reference loss scores are identical
      return a.value.localeCompare(b.value);
    }) as ParityStrategyBreakdown[];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isBeginReached() {
    const previousPeriod = this.historicalExtractDateMoment.subtract(
      HISTORICAL_POINTS_AMOUNT,
      this.dateAggregation,
    );
    return previousPeriod.isBefore(PARITY_STRATEGY_DATA_START);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isBreakdownRequestRunning() {
    return this.model.breakdowns.isRunning;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isEndReached() {
    const nextPeriod = this.historicalExtractDateMoment.add(
      1,
      this.dateAggregation,
    );
    return nextPeriod.isAfter(this.maxDateMoment);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isEvolutionRequestRunning() {
    return this.model.evolutions.isRunning;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isExportDisabled() {
    // Permanently disable export when filters have been selected
    // Temporarily disable export when the evolution request is still running
    return this.hasActiveFilters || !this.hasEvolutionData;
  }

  // Check whether the extract date from the gauge is visible in the evolution chart
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isExtractDateInRange() {
    return this.filteredEvolutionsForChart.some(
      (extractDate) => extractDate.id === this.currentExtractDate,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isHistoricalLatestSelected() {
    return this.historicalExtractDateMoment.isAfter(
      getFormattedLastPossibleExtractDate(),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isHotelSelected() {
    if (!this.hasBreakdownData) {
      return false;
    }

    return this.activeFilters.some((filter: string) =>
      filter.startsWith(DIMENSIONS.HOTEL),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isIssueRequestRunning() {
    return this.model.issues?.isRunning;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isLatestSelected() {
    return this.extractDateMoment.isAfter(
      getFormattedLastPossibleExtractDate(),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isLoading() {
    return (
      this.isEvolutionRequestRunning ||
      this.isBreakdownRequestRunning ||
      this.isIssueRequestRunning
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get issues() {
    if (!this.hasIssueData) {
      return [];
    }

    return this.model.issues?.value as ParityStrategyIssue[];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get mappedFilters() {
    if (this.isBreakdownRequestRunning) {
      return this.activeFilters.map(() => {
        return { values: [{ displayValue: '' }] };
      });
    }

    return this.activeFilters.map((filter: string) => {
      const [dimension, mappedValueId] = filter.split(FILTER_DELIMITER);

      const filterValue = this.breakdownData.find(
        (breakdown) =>
          breakdown.dimension === dimension &&
          breakdown.mappedValueId === mappedValueId,
      );
      // Display N/A if the filter value can't be found in the response
      const formattedFilter = filterValue
        ? filterValue.formattedValue
        : this.intl.t('general.not_applicable');

      return {
        key: filter,
        values: [{ displayValue: formattedFilter }],
        formattedValue: this.intl.t(
          DIMENSION_TRANSLATIONS[dimension as DimensionValue],
          {
            count: 1,
          },
        ),
      };
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get maxDateMoment() {
    return this.hotelAndMarketService
      .getLocalTime()
      .endOf(this.isoDateAggregation);
  }

  // The max value of all the loss, reference loss and benchmark scores in the breakdowns and promotions section
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get maxReferenceValue() {
    const promotionMax =
      this.model.breakdowns.value?.promotions.reduce((max, promotion) => {
        return Math.max(max, promotion.lossScoreAsInteger ?? 0);
      }, 0) ?? 0;

    const breakdownMax =
      this.model.breakdowns.value?.breakdowns.reduce((max, breakdown) => {
        return Math.max(max, breakdown.lossScoreAsInteger ?? 0);
      }, 0) ?? 0;

    return Math.max(promotionMax, breakdownMax);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get minDateMoment() {
    return this.hotelAndMarketService.toLocalTime(PARITY_STRATEGY_DATA_START);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get numberOfHotelWithIssues() {
    if (this.isBreakdownRequestRunning) {
      return 0;
    }

    return this.model.breakdowns.value?.meta.number_of_hotels_with_issues;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get numberOfIssues() {
    return this.issues?.length;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get previousPeriodData() {
    if (
      !this.hasEvolutionData ||
      !this.sortedEvolutions.includes(this.selectedPeriodData)
    ) {
      return {} as ParityStrategyEvolution;
    }

    return this.sortedEvolutions[
      this.selectedPeriodIndex - 1
    ] as ParityStrategyEvolution;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get promotionBlocks() {
    if (!this.hasBreakdownData) {
      return {} as Record<DimensionValue, BreakdownBlock>;
    }

    const promotions = {} as Record<DimensionValue, BreakdownBlock>;
    this.model.breakdowns.value?.promotions.forEach((promotion) => {
      if (promotions[promotion.dimension]) {
        // @ts-expect-error: Incorrect type
        promotions[promotion.dimension].data.push(promotion);
      } else {
        promotions[promotion.dimension] = {
          formattedDimension: promotion.formattedDimension,
          // @ts-expect-error: Incorrect type
          data: [promotion],
        };
      }
    });

    Object.keys(promotions).forEach((dimension) => {
      if (dimension === DIMENSIONS.LOS) {
        // Sort by LOS in ascending order
        promotions[dimension].data.sort(
          (a, b) => parseInt(a.value, 10) - parseInt(b.value, 10),
        );
      } else {
        // Sort by loss score in descending order
        promotions[dimension as DimensionValue].data.sort(
          (a, b) => (b.lossScoreAsInteger ?? 0) - (a.lossScoreAsInteger ?? 0),
        );
      }
    });

    // Sort the breakdowns according to the sortOrder
    const sortedPromotions = {} as Record<DimensionValue, BreakdownBlock>;
    PROMOTION_BLOCK_ORDER.forEach((dimension) => {
      if (promotions[dimension]) {
        sortedPromotions[dimension] = promotions[dimension];
      } else {
        sortedPromotions[dimension] = {
          formattedDimension: this.intl.t(DIMENSION_TRANSLATIONS[dimension], {
            count: 1,
          }),
          data: [],
        };
      }
    });

    return sortedPromotions;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedPeriodData() {
    if (!this.hasEvolutionData) {
      return {} as ParityStrategyEvolution;
    }

    if (this.isLatestSelected) {
      return (
        this.sortedEvolutions.find((evolution) => evolution.isLatest) ??
        ({} as ParityStrategyEvolution)
      );
    }

    return (
      this.sortedEvolutions.find(
        (evolution) => evolution.extractDate === this.extractDate,
      ) ?? ({} as ParityStrategyEvolution)
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedPeriodIndex() {
    return this.sortedEvolutions.indexOf(this.selectedPeriodData);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get sortedEvolutions() {
    if (!this.hasEvolutionData) {
      return [];
    }

    return [...(this.model.evolutions.value as ParityStrategyEvolution[])].sort(
      (a, b) => xValue(a.extractDate) - xValue(b.extractDate),
    );
  }

  @action addFilter(item: ParityStrategyBreakdown): void {
    const newFilters = this.activeFilters;
    const { dimension, mappedValueId } = item;

    const key = `${dimension}${FILTER_DELIMITER}${mappedValueId}`;
    const index = newFilters.indexOf(key);

    // If the filter is already active, remove it, otherwise add it
    if (index !== -1) {
      newFilters.splice(index, 1);
    } else {
      newFilters.push(key);

      if (PROMOTION_BLOCK_ORDER.includes(dimension)) {
        this.usageAnalytics.track('OTA Promotion Filter Applied', {
          eventTrigger: 'ota-promotion-widget',
          filterType: dimension,
          filterValue: mappedValueId,
        });
      } else if (dimension === DIMENSIONS.HOTEL) {
        this.usageAnalytics.track('Hotel Issues Explored', {
          eventTrigger: 'hotel-issues-widget',
          hotelId: parseInt(item.mappedValueId, 10),
        });
      }
    }

    this.router.transitionTo({ queryParams: { activeFilters: newFilters } });
  }

  @action clearActiveFilter(filterKey: string): void {
    const newFilters = this.activeFilters;
    const indexToDelete = newFilters.findIndex(
      (filter: string) => filter === filterKey,
    );

    if (indexToDelete !== -1) {
      newFilters.splice(indexToDelete, 1);
      this.router.transitionTo({ queryParams: { activeFilters: newFilters } });
    }
  }

  @action clearFilters(): void {
    this.router.transitionTo({ queryParams: { activeFilters: [] } });
  }

  @action handleExport(): void {
    type exportParams = {
      extract_date: string;
      extract_date_range_end?: string;
      extract_date_range_start?: string;
      'extract_dates[]'?: string[];
      market_id: string;
    };

    const requestParams: exportParams = {
      // @ts-expect-error: Type 'string | null' is not assignable to type 'string'.
      market_id: this.hotelAndMarketService.market.id,
      extract_date_range_start:
        this.filteredEvolutionsForChart.at(0)?.extractDate,
      extract_date_range_end:
        this.filteredEvolutionsForChart.at(-1)?.extractDate,
      extract_date: this.currentExtractDate,
    };

    if (!this.isExtractDateInRange) {
      requestParams['extract_dates[]'] = [this.currentExtractDate];
    }

    const basicExportUrl = formatUrl(
      '/apigateway/v1/app/download/paritystrategy',
      requestParams,
      this.ajax.host,
    );

    logReportInput(basicExportUrl, this.access.isStaffUser);

    openUrl(basicExportUrl);
  }

  @action setIsFilterBarHovered(isFilterBarHovered: boolean): void {
    this.isFilterBarHovered = isFilterBarHovered;
  }

  @action updateExtractDate(extractDate: string): void {
    this.router.transitionTo({ queryParams: { extractDate } });
  }

  @action updateHistoricalExtractDate(
    historicalExtractDateMoment: Dayjs,
  ): void {
    const isOutOfRange = historicalExtractDateMoment.isAfter(
      this.maxDateMoment,
      this.dateAggregation,
    );

    const historicalExtractDate = isOutOfRange
      ? this.maxDateMoment.format('YYYY-MM-DD')
      : historicalExtractDateMoment.format('YYYY-MM-DD');

    this.router.transitionTo({ queryParams: { historicalExtractDate } });
  }

  @action updateParityFilters(filter: ParityFilterOrBenchmark): void {
    if (filter === BENCHMARK) {
      this.isBenchmarkSelected = !this.isBenchmarkSelected;
    }
  }

  @action updateRange(selectedRange: { end: Dayjs; start: Dayjs }): void {
    const selectedPeriodAtStart = selectedRange.start;
    const formattedSelectedPeriodAtStart =
      selectedPeriodAtStart.format('YYYY-MM-DD');

    const queryParams: ParityStrategyQueryParams = {
      extractDate: formattedSelectedPeriodAtStart,
    };

    // Update the historicalExtractDate if the extractDate is outside of the current range (visible in the evolution chart)
    if (
      isExtractDateOutsideHistory(
        selectedPeriodAtStart,
        this.historicalExtractDate,
        this.dateAggregation,
      )
    ) {
      queryParams.historicalExtractDate = formattedSelectedPeriodAtStart;
    }

    this.router.transitionTo({ queryParams });
  }
}
